import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, IndianRupee, Receipt, Copy, ExternalLink, Users } from "lucide-react";

export default function AmbassadorDashboard() {
  const [me, setMe] = useState(null);
  const [txns, setTxns] = useState([]);
  const [refs, setRefs] = useState([]);

  useEffect(() => { (async () => {
    try {
      const [m, t, r] = await Promise.all([
        api.get("/ambassador/me"),
        api.get("/ambassador/transactions"),
        api.get("/ambassador/referrals"),
      ]);
      setMe(m.data); setTxns(t.data || []); setRefs(r.data || []);
    } catch { toast.error("Failed to load dashboard"); }
  })(); }, []);

  if (!me) return <DashboardLayout><div className="p-10 text-slate-400">Loading...</div></DashboardLayout>;

  const code = me.referral_code || "";
  const active = (me.ambassador_status || "active") === "active";
  const shareUrl = `https://pmrwelfarefoundation.org/test-series?ref=${code}`;
  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Copied"); } catch {} };

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-black inline-flex items-center gap-2">
        <GraduationCap size={22} /> Ambassador Dashboard
      </h1>
      <p className="text-sm text-slate-500 mt-1">Welcome, {me.name}. Track your referrals and earnings.</p>

      {/* Hero / referral code */}
      <div className={`mt-6 p-6 rounded-2xl ${active ? "bg-gradient-to-r from-blue-700 to-amber-600" : "bg-slate-700"} text-white`} data-testid="ambassador-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-black opacity-80">Your Unique Referral Code</div>
            <div className="mt-3 flex items-center gap-3">
              <span className="font-mono font-black text-4xl tracking-wider" data-testid="amb-self-code">{code}</span>
              <button onClick={() => copy(code)} className="bg-white/20 hover:bg-white/30 rounded-lg p-2"><Copy size={16} /></button>
            </div>
            <div className="mt-3 text-sm opacity-90">
              Share this code with classmates. They get <b>10% off</b>, you earn <b>10%</b>.
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="bg-white/20 rounded-full px-2 py-1 font-mono truncate">{shareUrl}</span>
              <button onClick={() => copy(shareUrl)} className="bg-white/20 hover:bg-white/30 rounded p-1.5"><Copy size={12} /></button>
            </div>
          </div>
          <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded ${active ? "bg-emerald-500" : "bg-red-500"}`}>
            {active ? "Active" : "Inactive — Contact admin"}
          </span>
        </div>
      </div>

      {/* Wallet stats */}
      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <StatCard label="Wallet Balance" value={Number(me.ambassador_wallet_balance || 0)} hint="payable" tone="emerald" />
        <StatCard label="Total Earned" value={Number(me.ambassador_total_earned || 0)} hint="lifetime" tone="brand" />
        <StatCard label="Paid Out" value={Number(me.ambassador_total_paid_out || 0)} hint="till date" tone="slate" />
      </div>

      {/* Referrals */}
      <h2 className="heading mt-10 text-lg font-black inline-flex items-center gap-2"><Users size={18} /> Successful Referrals ({refs.length})</h2>
      <div className="mt-3 rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Date</th>
              <th className="p-3 font-semibold">Test Series</th>
              <th className="p-3 font-semibold text-right">Sale Amount</th>
              <th className="p-3 font-semibold text-right">Your 10%</th>
            </tr>
          </thead>
          <tbody>
            {refs.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">No referral sales yet — share your code to start earning.</td></tr>}
            {refs.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`ref-row-${r.id}`}>
                <td className="p-3 text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-3 font-semibold">{r.test_id}</td>
                <td className="p-3 text-right font-mono">₹{Number(r.amount || 0).toFixed(0)}</td>
                <td className="p-3 text-right font-mono font-bold text-emerald-700">₹{(Number(r.base_amount || r.amount || 0) * 0.1).toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Wallet log */}
      <h2 className="heading mt-10 text-lg font-black inline-flex items-center gap-2"><Receipt size={18} /> Wallet Transactions</h2>
      <div className="mt-3 rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Date</th>
              <th className="p-3 font-semibold">Type</th>
              <th className="p-3 font-semibold text-right">Amount</th>
              <th className="p-3 font-semibold">Note</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">No transactions yet.</td></tr>}
            {txns.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`amb-self-txn-${t.id}`}>
                <td className="p-3 text-slate-500">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${t.type === "credit" ? "bg-emerald-100 text-emerald-700" : t.type === "payout" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                    {t.type}
                  </span>
                </td>
                <td className={`p-3 text-right font-mono font-bold ${t.amount > 0 ? "text-emerald-700" : t.amount < 0 ? "text-red-600" : "text-slate-400"}`}>
                  ₹{Math.abs(Number(t.amount || 0)).toFixed(2)}
                </td>
                <td className="p-3 text-slate-600 text-xs">{t.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ label, value, hint, tone }) {
  const colors = {
    emerald: "from-emerald-500 to-emerald-700",
    brand: "from-blue-700 to-blue-900",
    slate: "from-slate-600 to-slate-800",
  }[tone] || "from-slate-600 to-slate-800";
  return (
    <div className={`p-5 rounded-xl text-white bg-gradient-to-br ${colors}`}>
      <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">{label}</div>
      <div className="mt-2 font-black text-3xl flex items-center"><IndianRupee size={22} />{value.toFixed(0)}</div>
      <div className="text-xs mt-1 opacity-80">{hint}</div>
    </div>
  );
}
